import React, { ReactNode, useEffect, useRef, useState } from 'react'

import { iconFor } from '../../icons'
import { Box } from '../box'
import { CarouselIndicator } from '../carousel-indicator'
import { HStack } from '../hstack'
import { Icon } from '../icon'
import { Modal } from '../modal'
import { Pressable } from '../pressable'

export type PreviewAttachment = {
    link?: string
    name?: string | null
    type?: string | null
}

export type AttachmentReplacement = {
    url: string
    data: Blob
}

export type AttachmentPreviewModalProps = {
    isOpen: boolean
    attachments: PreviewAttachment[]
    attachmentIndex: number
    editable: boolean
    closeAction(): void
    onIndexChange(index: number): void
    updateAttachment?(payload: AttachmentReplacement): void
}

export const isImageAttachment = (attachment: PreviewAttachment) =>
    !attachment.type || attachment.type.startsWith('image/')

const mediaClass = 'pointer-events-auto max-h-full max-w-full'

const PreviewMedia = ({ attachment }: { attachment: PreviewAttachment }) => {
    if (attachment.type === 'video/mp4') {
        // eslint-disable-next-line jsx-a11y/media-has-caption -- user uploads carry no caption track
        return <video src={attachment.link} controls autoPlay className={mediaClass} />
    }
    if (attachment.type === 'application/pdf') {
        return (
            <iframe
                src={`${attachment.link}#toolbar=0`}
                title={attachment.name ?? ''}
                className='pointer-events-auto h-full w-full border-0'
            />
        )
    }
    return <img src={attachment.link} alt={attachment.name ?? ''} className={mediaClass} />
}

const drawRotated = (canvas: HTMLCanvasElement, image: HTMLImageElement, angle: number) => {
    const context = canvas.getContext('2d')
    if (!context) {
        return
    }
    const isQuarterTurn = angle % 180 !== 0
    canvas.width = isQuarterTurn ? image.height : image.width
    canvas.height = isQuarterTurn ? image.width : image.height
    context.save()
    context.translate(canvas.width / 2, canvas.height / 2)
    context.rotate((angle * Math.PI) / 180)
    context.drawImage(image, -image.width / 2, -image.height / 2)
    context.restore()
}

export const nextAngle = (angle: number, delta: number) => (angle + delta + 360) % 360

type ToolbarAction = { icon: string; label: string; onPress?(): void }

const EditToolbar = ({ actions }: { actions: ToolbarAction[] }) => (
    <HStack className='rounded-sm bg-background-elevation'>
        {actions.map(action => (
            <Pressable
                key={action.icon}
                accessibilityRole='button'
                accessibilityLabel={action.label}
                disabled={!action.onPress}
                onPress={action.onPress}
                className={action.onPress ? 'm-2' : 'm-2 opacity-30'}
            >
                <Icon as={iconFor(action.icon)} size={24} className='text-content-default' />
            </Pressable>
        ))}
    </HStack>
)

const Stage = ({ children }: { children: ReactNode }) => (
    <Box className='pointer-events-none flex-1 items-center justify-center'>
        <Box className='h-4/5 w-4/5 items-center justify-center'>{children}</Box>
    </Box>
)

const Controls = ({ children }: { children: ReactNode }) => (
    <Box className='pointer-events-auto absolute bottom-6 items-center gap-4 self-center'>
        {children}
    </Box>
)

const RotatableImage = ({
    link,
    onSave,
    pager,
}: {
    link: string
    onSave?(payload: AttachmentReplacement): void
    pager: ReactNode
}) => {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const [image, setImage] = useState<HTMLImageElement>()
    const [angle, setAngle] = useState(0)
    const [isSaving, setSaving] = useState(false)

    useEffect(() => {
        const next = new window.Image()
        next.crossOrigin = 'Anonymous'
        next.onload = () => setImage(next)
        next.src = link
        return () => {
            next.onload = null
        }
    }, [link])

    useEffect(() => {
        if (canvasRef.current && image) {
            drawRotated(canvasRef.current, image, angle)
        }
    }, [image, angle])

    const save = () => {
        setSaving(true)
        canvasRef.current?.toBlob(data => {
            if (data) {
                onSave?.({ url: link, data })
            }
        })
    }

    const isRotated = angle !== 0
    return (
        <>
            <Stage>
                <canvas ref={canvasRef} className={`${mediaClass} mx-auto block w-auto`} />
            </Stage>
            <Controls>
                <EditToolbar
                    actions={[
                        {
                            icon: 'RotateLeft',
                            label: 'Rotate left',
                            onPress: () => setAngle(a => nextAngle(a, -90)),
                        },
                        {
                            icon: 'RotateRight',
                            label: 'Rotate right',
                            onPress: () => setAngle(a => nextAngle(a, 90)),
                        },
                        {
                            icon: 'Close',
                            label: 'Reset',
                            onPress: isRotated ? () => setAngle(0) : undefined,
                        },
                        {
                            icon: 'Done',
                            label: 'Save',
                            onPress: isRotated && !isSaving && onSave ? save : undefined,
                        },
                    ]}
                />
                {pager}
            </Controls>
        </>
    )
}

/**
 * Full-screen attachment viewer: image, mp4 or pdf with prev/next paging (arrow
 * keys too). `editable` images get the rotate/reset/save toolbar, saving the
 * rotated bitmap through `updateAttachment`. Web only.
 */
export const AttachmentPreviewModal = ({
    isOpen,
    attachments,
    attachmentIndex,
    editable,
    closeAction,
    onIndexChange,
    updateAttachment,
}: AttachmentPreviewModalProps) => {
    const attachment = attachments[attachmentIndex]
    const count = attachments.length
    const goTo = (index: number) => index >= 0 && index < count && onIndexChange(index)
    const isEditing = editable && !!attachment && isImageAttachment(attachment) && !!attachment.link

    useEffect(() => {
        if (!isOpen) {
            return undefined
        }
        const onKey = (event: KeyboardEvent) => {
            if (event.key === 'ArrowLeft') {
                goTo(attachmentIndex - 1)
            }
            if (event.key === 'ArrowRight') {
                goTo(attachmentIndex + 1)
            }
        }
        window.addEventListener('keydown', onKey)
        return () => window.removeEventListener('keydown', onKey)
    })

    const pager = count > 1 && (
        <CarouselIndicator
            count={count}
            activeIndex={attachmentIndex}
            onPrev={() => goTo(attachmentIndex - 1)}
            onNext={() => goTo(attachmentIndex + 1)}
            onDotPress={onIndexChange}
        />
    )

    return (
        <Modal
            isOpen={isOpen && !!attachment}
            onClose={closeAction}
            size='full'
            scrollable={false}
            className='h-full rounded-none border-0 bg-transparent'
            bodyClassName='bg-transparent'
        >
            <Pressable className='absolute inset-0 cursor-default' onPress={closeAction} />
            {isEditing && (
                <RotatableImage
                    key={attachment.link}
                    link={attachment.link ?? ''}
                    onSave={updateAttachment}
                    pager={pager}
                />
            )}
            {!isEditing && attachment && (
                <>
                    <Stage>
                        <PreviewMedia attachment={attachment} />
                    </Stage>
                    <Controls>{pager}</Controls>
                </>
            )}
        </Modal>
    )
}
